import { Howl } from 'howler';
import type { controller } from 'axios';
import { convertToSlug } from '../api/utils';
import axios from 'axios';
import { LoopStatus, type Music } from '../types/plugins/music.model';
import { ReportingTicket } from '~/api/reporting';

function shuffleArray(array: any[]) {
  var currentIndex = array.length,
    randomIndex;

  // While there remain elements to shuffle...
  while (currentIndex != 0) {
    // Pick a remaining element...
    randomIndex = Math.floor(Math.random() * currentIndex);
    currentIndex--;
    // And swap it with the current element.
    [array[currentIndex], array[randomIndex]] = [
      array[randomIndex],
      array[currentIndex],
    ];
  }

  return array;
}

export default defineNuxtPlugin((app) => {
  const { $subscription, $auth, $config, $globals, $api, $t, $snackbar, $customGtm } =
    app;
  const { purchaseHandling, checkIfCanConsume } = usePurchase();
  const { userHistoryHandler } = useUserHistory();
  const musicReactive: Music = reactive({
    isPlaying: false,
    playlist: [] as any[],
    currentTrack: null as null | Howl,
    isPreview: false,
    currentIndex: 0,
    timer: 0,
    currentPlaylistId: 0 as string | number,
    currentTrackId: 0 as number,
    basePlaylist: [] as any[],
    shuffled: false,
    loopStatus: LoopStatus.OFF as LoopStatus,
    durationSeconds: 0,
    volume: 0.5,
    muted: false,
    controller: null as AbortController | null,
    seeked: false,
    requestedSeek: 0,
    lastTracked: 0,
    nextCheck: 0,
    isEnded: false,
    reporting: null,
    songPurchaseIds: null,
    lastElementToFocus: null as HTMLInputElement | null,
    // Methods
    musicCTAText(article: any, type?: string) {
      // in case the article has offer attributes to check on the subscription types
      const articleOfferAttributes =
        $subscription.articleHasOfferAttributes(article);
      const isUserSubscribedToArticle = $subscription.isUserSubscribedToArticle(
        articleOfferAttributes,
        'music',
      );
      if (!$auth.isLoggedIn) {
        return 'music.signIn';
      } else if (articleOfferAttributes?.length && isUserSubscribedToArticle) {
        return 'music.ListenNow';
      } else if (articleOfferAttributes?.length && !isUserSubscribedToArticle) {
        return $subscription.userWallet &&
          $subscription.userWallet.includes(+article.id)
          ? 'music.ListenNow'
          : 'music.NoActiveSubscriptionBtn';
      } else if (!articleOfferAttributes || !articleOfferAttributes.length) {
        return type !== 'track' &&
          $subscription.userWallet &&
          $subscription.userWallet.includes(+article.id)
          ? 'playAll'
          : 'play';
      } else {
        return 'music.ListenNow';
      }
    },
    async setPlaylist({
      list = [] as any,
      autoPlay = false,
      id = 0,
      startFrom = 0,
      element = null,
      shuffle = false,
    }): Promise<void> {
      musicReactive.lastElementToFocus = element;
      await musicReactive.stop();
      musicReactive.shuffled = false;
      musicReactive.loopStatus = LoopStatus.OFF;

      if ($api?.tenant?.default?.FTMDOnly) {
        // is FTMDOnly
        const trackToPlay = list[startFrom];
        const playableSongs = list.filter((item) =>
          $subscription.userWallet?.includes(+item.id),
        );
        const newIndex = playableSongs.indexOf(trackToPlay);
        if (newIndex >= 0) {
          list = playableSongs;
          startFrom = newIndex;
        }
      }

      musicReactive.basePlaylist = [...list];
      musicReactive.playlist = [...list];
      musicReactive.currentPlaylistId = id;
      musicReactive.currentIndex = startFrom;
      if (musicReactive.currentTrack) {
        musicReactive.currentTrack.unload();
      }
      musicReactive.currentTrack = null;
      if (shuffle) {
        musicReactive.shuffle();
      }

      if (await musicReactive.setTrack()) {
        if (autoPlay) {
          musicReactive.play();
        }
      }
    },
    async play(): Promise<boolean> {
      if ($globals.currentPlayingVideo) {
        try {
          $globals.currentPlayingVideo.pause();
        } catch (error) {
          // video not found skipping
        }
      }

      if (musicReactive.currentTrack) {
        if (!musicReactive.isEnded) {
          if (musicReactive.currentTrack.playing()) {
            musicReactive.currentTrack.stop();
          }
          musicReactive.currentTrack.play();
          $customGtm?.track({
            ET: 'Event',
            EV: 'play',
            EC: 'Music Player Tracking',
            EA: musicReactive.track.title,
          });
          musicReactive.isPlaying = true;
        } else {
          musicReactive.isEnded = false;
          if (await musicReactive.setTrack()) {
            musicReactive.play();
          }
        }
        localStorage.setItem('currentTrackTime', Date.now().toString());
      }

      return true;
    },
    async toggleMute(): Promise<void> {
      if (musicReactive.currentTrack) {
        musicReactive.muted = !musicReactive.muted;
        musicReactive.currentTrack.mute(musicReactive.muted);
      }
    },
    async toggle(): Promise<boolean> {
      if (!musicReactive.currentTrack) {
        return false;
      }
      if (musicReactive.currentTrack.playing()) {
        musicReactive.pause();
      } else {
        musicReactive.play();
      }
      return true;
    },
    pause(): void {
      if (musicReactive.currentTrack) {
        musicReactive.currentTrack.pause();
        $customGtm?.track({
          ET: 'Event',
          EV: 'Paused',
          EC: 'Music Player Tracking',
          EA: musicReactive.track.title,
        });
        musicReactive.isPlaying = false;
      }
    },
    async stop(): Promise<boolean> {
      if (musicReactive.currentTrack) {
        musicReactive.currentTrack.stop();
        musicReactive.isPlaying = false;
      }
      return true;
    },
    async next(): Promise<void> {
      if (musicReactive.currentIndex >= musicReactive.playlist.length - 1) {
        if (musicReactive.loopStatus === LoopStatus.REPEAT_ALL) {
          await musicReactive.stop();
          musicReactive.currentIndex = 0;
          if (await musicReactive.setTrack()) {
            await musicReactive.play();
          }
        } else {
          musicReactive.isEnded = true;
        }
      } else {
        // TODO: check if isPreview here
        await musicReactive.stop();
        musicReactive.currentIndex = musicReactive.currentIndex + 1;
        if (await musicReactive.setTrack()) {
          await musicReactive.play();
        }
      }
    },
    async loadMoreCollectionTracks() {
      if (musicReactive?.track?.contentType == 'AUDIO_BOOK_CHAPTER') {
        try {
          const res = await $api.endPoints.getStructure(
            `fragments_get-articles`,
            {
              offset: musicReactive.basePlaylist?.length,
              limit: 10,
              id: musicReactive.track?.collectionInfo?.articleId,
            },
          );
          const list = res[0]?.data?.entries;
          if (list?.length) {
            musicReactive.basePlaylist = [
              ...musicReactive.basePlaylist,
              ...list,
            ];
            musicReactive.playlist = [...musicReactive.playlist, ...list];
          }
        } catch (error) {}
      }
    },
    setVolume(volume: number): void {
      musicReactive.volume = volume;
      if (musicReactive.currentTrack) {
        musicReactive.currentTrack.volume(volume);
      }
    },
    async prev(): Promise<void> {
      await musicReactive.stop();
      musicReactive.currentIndex =
        musicReactive.currentIndex === 0
          ? musicReactive.playlist.length - 1
          : musicReactive.currentIndex - 1;
      if (await musicReactive.setTrack()) {
        await musicReactive.play();
      }
    },
    async setTrack(): Promise<boolean> {
      if (musicReactive.controller) {
        musicReactive.controller.abort();
      }
      musicReactive.currentTrackId =
        musicReactive.playlist[musicReactive.currentIndex].id;
      musicReactive.controller = new AbortController();
      const signal = musicReactive.controller.signal;
      try {
        musicReactive.songPurchaseIds = null;
        const song = (await $api.endPoints.getMusicTrack(
          musicReactive.playlist[musicReactive.currentIndex],
          signal,
        )) as {
          isPreview: boolean;
          purchase: [];
          ticket: string;
          url: string;
        };
        musicReactive.controller = null;
        if (musicReactive.currentTrack) {
          musicReactive.currentTrack.unload();
        }
        musicReactive.songPurchaseIds = song.purchase;
        musicReactive.isPreview = song.isPreview;
        musicReactive.songTicketId = song.ticket;
        if (!song.url && song.purchase && song.purchase.length) {
          purchaseHandling(
            song.purchase,
            false,
            musicReactive.playlist[musicReactive.currentIndex],
          );
          // check for offerIDs, and no URL
          return false;
        }

        if (!song.isPreview && !song.url) {
          musicReactive.closeMusicMiniPlayer();
          return false;
        }
        userHistoryHandler(musicReactive.playlist[musicReactive.currentIndex]);
        musicReactive.currentTrack = new Howl({
          format: ['mp3'],
          html5: true,
          src: [song.url],
          volume: musicReactive.volume,
          onload: () => {
            musicReactive.durationSeconds = Math.round(
              musicReactive.currentTrack?.duration() || 0,
            );
            musicReactive.reporting.ticketId = song.ticket;
            musicReactive.reporting.handleReporting(true);
          },
          onplay: () => {
            musicReactive.isPlaying = true;
            musicReactive.reporting.handleReporting();
          },
          onend: async () => {
            musicReactive.stop();
            if (musicReactive.isPreview) {
              if (musicReactive.songPurchaseIds) {
                // show subdialog this songPurchaseIds
                purchaseHandling(
                  song.purchase,
                  false,
                  musicReactive.playlist[musicReactive.currentIndex],
                );
                // check for offerIDs, and no URL
                return false;
              } else {
                if (!$api?.tenant?.default?.FTMDOnly) {
                  await checkIfCanConsume(
                    musicReactive.track,
                    musicReactive.track?.id,
                  );
                }
              }
            } else {
              musicReactive.reporting.endReporting();
              if (musicReactive.loopStatus === LoopStatus.REPEAT_ONE) {
                // musicReactive.play();
                if (await musicReactive.setTrack()) {
                  await musicReactive.play();
                }
              } else {
                musicReactive.next();
              }
            }
          },
          onpause: () => {
            musicReactive.isPlaying = false;
            musicReactive.reporting.pauseReporting();
          },
        });
        musicReactive.currentTrack.mute(musicReactive.muted);
        // Handle Device actions
        const node = (musicReactive.currentTrack as any)._sounds[0]._node;
        node.onpause = (e: any) => {
          if (!musicReactive.seeked) {
            musicReactive.isPlaying = false;
          }
        };
        node.onplay = (e: any) => {
          if (!musicReactive.seeked) {
            musicReactive.isPlaying = true;
          }
        };
        if (
          !musicReactive.isPreview &&
          musicReactive.currentIndex >= musicReactive.playlist.length - 1
        ) {
          await musicReactive.loadMoreCollectionTracks();
        }
        return true;
      } catch (error) {
        return false;
      }
    },
    async selectTrack(index: string | number): Promise<void> {
      musicReactive.currentIndex = +index;
      if (await musicReactive.setTrack()) {
        musicReactive.play();
      }
    },
    shuffle(): void {
      let track: any;
      if (musicReactive.currentTrack) {
        track = musicReactive.playlist[musicReactive.currentIndex];
      }
      musicReactive.shuffled = !musicReactive.shuffled;
      if (musicReactive.shuffled) {
        musicReactive.playlist = shuffleArray(musicReactive.playlist);
      } else {
        musicReactive.playlist = [...musicReactive.basePlaylist];
      }
      if (track) {
        musicReactive.currentIndex = musicReactive.playlist.findIndex(
          (i) => i.id === track.id,
        );
      }
      $customGtm.track({
        ET: 'Event',
        EV: 'Click to shuffle',
        EA: musicReactive.track.title,
        EC: 'Music Player Tracking',
      });
    },
    seek(seconds: number): void {
      if (musicReactive.currentTrack) {
        musicReactive.requestedSeek = seconds;
        musicReactive.seeked = true;
        musicReactive.currentTrack.seek(seconds);
        musicReactive.reporting.tmpMaxDuration = 0;
        setTimeout(() => {
          musicReactive.requestedSeek = 0;
          musicReactive.seeked = false;
        }, 200);
      }
    },
    toggleLoop(): void {
      if (musicReactive.loopStatus === LoopStatus.OFF) {
        musicReactive.loopStatus = LoopStatus.REPEAT_ALL;
      } else if (musicReactive.loopStatus === LoopStatus.REPEAT_ALL) {
        musicReactive.loopStatus = LoopStatus.REPEAT_ONE;
      } else if (musicReactive.loopStatus === LoopStatus.REPEAT_ONE) {
        musicReactive.loopStatus = LoopStatus.OFF;
      }
    },
    sortItem(oldIndex: number, newIndex: number): void {
      if (
        (oldIndex > musicReactive.currentIndex &&
          newIndex < musicReactive.currentIndex) ||
        (oldIndex > musicReactive.currentIndex &&
          newIndex === musicReactive.currentIndex)
      ) {
        musicReactive.currentIndex++;
      } else if (
        (oldIndex < musicReactive.currentIndex &&
          newIndex === musicReactive.currentIndex) ||
        (oldIndex < musicReactive.currentIndex &&
          newIndex > musicReactive.currentIndex)
      ) {
        musicReactive.currentIndex--;
      } else if (oldIndex === musicReactive.currentIndex) {
        musicReactive.currentIndex = newIndex;
      }
    },
    async removeTrack(index: number): Promise<void> {
      const track = musicReactive.playlist[index];
      const indexFromBasePlaylist = musicReactive.basePlaylist.findIndex(
        (i) => track.id === i.id,
      );
      musicReactive.basePlaylist.splice(indexFromBasePlaylist, 1);
      musicReactive.playlist.splice(index, 1);
      if (
        musicReactive.basePlaylist.length < 1 &&
        musicReactive.playlist.length < 1
      ) {
        await musicReactive.stop();
        musicReactive.currentTrackId = 0;
        return;
      } else {
        if (index < musicReactive.currentIndex) {
          musicReactive.currentIndex--;
        } else if (index === musicReactive.currentIndex) {
          await musicReactive.setTrack();
          if (musicReactive.isPlaying) {
            musicReactive.play();
          }
        }
      }
    },
    addToQueue(track: { id: number }): void {
      const user = $auth.user as { roles: string[] };
      if (user?.roles[0] === 'user') {
        if (musicReactive.playlist.length < 1) {
          musicReactive.playlist.push(track);
          musicReactive.basePlaylist.push(track);
          musicReactive.selectTrack(musicReactive.currentIndex);
        } else {
          musicReactive.playlist.push(track);
          musicReactive.basePlaylist.push(track);
          $snackbar.snackbar = true;
          $snackbar.text = $t('addQueueTrackSuccess');
        }
      } else {
        $snackbar.snackbar = true;
        $snackbar.requireAuth = true;
        $snackbar.text = $t('addQueueNotLogged');
      }
    },
    playSingleTrack(track: { id: number }, element: EventTarget): void {
      musicReactive.lastElementToFocus = element;
      musicReactive.playlist.push(track);
      musicReactive.basePlaylist.push(track);
      musicReactive.currentIndex = musicReactive.playlist.indexOf(track);
      musicReactive.selectTrack(musicReactive.currentIndex);
    },
    closeMusicMiniPlayer() {
      musicReactive.stop();
      musicReactive.playlist = [];
      musicReactive.currentIndex = 0;
      musicReactive.currentTrack = null;
      musicReactive.currentPlaylistId = 0;
      musicReactive.currentTrackId = 0;
    },
    artistPath(artistId: number, artistName: string) {
      return `/music/artists/${convertToSlug(artistName)}/${artistId}`;
    },
    albumPath(
      albumId: number,
      artistId: number,
      artistName: string,
      albumTitle: string,
    ) {
      return `/music/albums/${convertToSlug(
        artistName,
      )}/${artistId}/${convertToSlug(albumTitle)}/${albumId}`;
    },
    trackPath(trackId: number, trackTitle: string) {
      return `/music/tracks/${convertToSlug(trackTitle) ? convertToSlug(trackTitle) + '/' : ''}${trackId}`;
    },
    playlistPath(playlistId: number, playlistTitle: string) {
      return `/music/playlists/${convertToSlug(playlistTitle)}/${playlistId}`;
    },
    podcastPath(podcastId: number, podcastTitle: string) {
      return `/podcast/details/${convertToSlug(podcastTitle)}/${podcastId}`;
    },
    episodePath(episodeId: number, episodeTitle: string) {
      return `/podcast/episode/${convertToSlug(episodeTitle)}/${episodeId}`;
    },
    audiobookPath(audiobookId: number, audiobookTitle: string) {
      return `/audiobook/details/${convertToSlug(
        audiobookTitle,
      )}/${audiobookId}`;
    },
    audiobookArtistPath(artistId: number, artistTitle: string) {
      return `/audiobook/artists/${convertToSlug(artistTitle)}/${artistId}`;
    },
    checkPlayingTrack(event: any) {
      if (event.key == 'currentTrackTime') {
        musicReactive.toggle();
      }
    },
    // Computed
    position: computed(() => {
      if (musicReactive.currentTrack && musicReactive.timer) {
        const seconds = Math.round(musicReactive.currentTrack.seek());
        const data = new Date(seconds * 1000).toISOString();
        if (seconds < 3600) {
          return data.substring(14, 14 + 5);
        } else {
          return data.substring(11, 11 + 8);
        }
      }
      return '00:00';
    }),
    duration: computed(() => {
      const date: string = new Date(
        musicReactive.durationSeconds * 1000,
      ).toISOString();
      if (musicReactive.durationSeconds < 3600) {
        return date.substring(14, 14 + 5);
      } else {
        return date.substring(11, 11 + 8);
      }
    }),
    positionSeconds: computed(() => {
      if (musicReactive.seeked) {
        return musicReactive.requestedSeek;
      }
      if (musicReactive.currentTrack && musicReactive.timer) {
        const seconds = musicReactive.currentTrack.seek();
        return seconds;
      }
      return 0;
    }),
    hasNext: computed(() => {
      const isLast =
        musicReactive.currentIndex >= musicReactive.playlist.length - 1;
      if (!isLast) {
        return true;
      } else if (musicReactive.loopStatus === LoopStatus.REPEAT_ALL && isLast) {
        return true;
      }
      return false;
    }),
    hasPrev: computed(() => {
      const isFirst = musicReactive.currentIndex === 0;
      if (!isFirst) {
        return true;
      } else if (
        musicReactive.loopStatus === LoopStatus.REPEAT_ALL &&
        isFirst
      ) {
        return true;
      }
      return false;
    }),
    track: computed(() => {
      return musicReactive.playlist[musicReactive.currentIndex];
    }),
  });

  // created
  if (process.client) {
    window.addEventListener('storage', musicReactive.checkPlayingTrack);
    setInterval(() => {
      musicReactive.timer++;
    }, 100);
  }
  // created END
  musicReactive.reporting = new ReportingTicket($api);
  if (import.meta.client) {
    watch(
      () => musicReactive.positionSeconds,
      (e) => {
        const trackerTimes = [25, 50, 75, 100];
        const duration =
          (musicReactive.positionSeconds / musicReactive.durationSeconds) * 100;
        if (duration < musicReactive.nextCheck) {
          musicReactive.nextCheck = 0;
          musicReactive.lastTracked =
            [...trackerTimes].reverse().find((i) => i <= duration) || 0;
        }
        if (
          duration - musicReactive.nextCheck < 5 &&
          !(duration >= Math.floor(musicReactive.durationSeconds))
        ) {
          return;
        }
        const step = trackerTimes.find(
          (i) => i <= duration && i > musicReactive.lastTracked,
        );
        if (step) {
          musicReactive.lastTracked = step;
          $customGtm.track({
            ET: 'Event',
            EA: musicReactive.track.title,
            EV: `Progress ${step}%`,
            EC: 'Music Player Tracking',
          });
        }
        musicReactive.nextCheck = duration;
      },
    );

    watch(
      () => musicReactive.isPlaying,
      (e) => {
        if (musicReactive?.reporting) {
          musicReactive.reporting.isPlaying = e;
        }
      },
    );
  }
  return {
    provide: {
      music: musicReactive,
    },
  };
});

// const musicPlugin: Plugin = (context, inject) => {
//   inject('music', new MusicPlayer(context));
// };

// export default musicPlugin;
